import { useState, useRef, useEffect } from 'react'
import { gql } from 'graphql-request'
import { Link, useNavigate } from 'react-router-dom'
import { PlusIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { Upload } from 'lucide-react'
import { useQuery, useMutation } from '@hooks/graphql'
import { useSubscription } from '../../hooks/subscription'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu'

const CLASSROOMS_QUERY = gql`
  query classrooms {
    classrooms {
      id
      name
    }
  }
`

const ASSIGN_TUTOR_TO_CLASSROOM_MUTATION = gql`
  mutation assignTutorToClassroom($input: AssignToClassroomInput!) {
    assignTutorToClassroom(input: $input) {
      assignment {
        id
        classroom {
          id
        }
      }
      errors {
        message
      }
    }
  }
`

const AssignDialog = ({ tutorId, name, refetch }) => {
  const navigate = useNavigate()
  const { hasProFeatures } = useSubscription()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedClassroom, setSelectedClassroom] = useState(null)
  const dialogTriggerRef = useRef(null)
  const dropdownTriggerRef = useRef(null)
  const [menuWidth, setMenuWidth] = useState(0)

  useEffect(() => {
    if (dropdownTriggerRef.current) {
      const updateWidth = () => {
        setMenuWidth(dropdownTriggerRef.current.offsetWidth)
      }

      updateWidth()
      window.addEventListener('resize', updateWidth)

      return () => {
        window.removeEventListener('resize', updateWidth)
      }
    }
  }, [dropdownTriggerRef, isOpen])

  const { data: { classrooms: educatorClassrooms = [] } = {} } = useQuery({
    gqlQuery: CLASSROOMS_QUERY
  })

  const { mutate: assign, isLoading: isAssigning } = useMutation({
    gqlMutation: ASSIGN_TUTOR_TO_CLASSROOM_MUTATION,
    onSuccess: data => {
      const { id: assignmentId, classroom: { id: classroomId } } = data.assignTutorToClassroom.assignment
      navigate(`/educators/classrooms/${classroomId}/tutors/${tutorId}/sessions/${assignmentId}`)
      setIsOpen(false)
      refetch()
    }
  })

  const handleAssign = () => {
    if (selectedClassroom) {
      assign({ input: { tutorId, classroomId: selectedClassroom.id } })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          id='assign-tutor-button'
          disabled={!hasProFeatures}
          variant='default'
          className='flex h-10 rounded-md px-4 py-2 gap-2 mr-2 pro-feature'
          data-tutorial='assign-tutor-step'
          ref={dialogTriggerRef}
        >
          Assign
          <Upload className='h-4 w-4' />
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Assign Tutor</DialogTitle>
          <DialogDescription>
            You're about to create a session for <strong>{name}</strong>. Select a classroom below.
          </DialogDescription>
        </DialogHeader>

        <DropdownMenu>
          <div className='w-full'>
            <DropdownMenuTrigger asChild>
              <Button
                variant='outline'
                className='w-full justify-between'
                ref={dropdownTriggerRef}
              >
                {selectedClassroom ? selectedClassroom.name : 'Select a classroom'}
                <ChevronDownIcon className='h-4 w-4 opacity-50' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='start'
              className='w-[var(--radix-dropdown-menu-trigger-width)]'
              style={{ minWidth: menuWidth ? `${menuWidth}px` : 'auto' }}
            >
              <DropdownMenuLabel>Available Classrooms</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {educatorClassrooms.length > 0 ? (
                educatorClassrooms.map(classroom => (
                  <DropdownMenuItem
                    key={classroom.id}
                    onSelect={() => setSelectedClassroom(classroom)}
                    className={`${selectedClassroom?.id === classroom.id ? 'bg-accent text-accent-foreground' : ''}`}
                  >
                    {classroom.name}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem disabled>No classrooms available</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </div>
        </DropdownMenu>

        <Link to='/educators/classrooms' className='flex items-center text-primary text-sm'>
          <PlusIcon className='h-4 w-4 mr-1.5' />
          Add new classroom
        </Link>

        <DialogFooter>
          <Button
            variant='default'
            loading={isAssigning}
            disabled={isAssigning || !selectedClassroom}
            onClick={handleAssign}
            className='w-fit'
          >
            {isAssigning ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AssignDialog 
